import type { PrimaryControlsStripBackAppShellPlacement } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.back";
import type { Tagged } from "@-/com.npmjs.type-fest";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import {
  usePrimaryControlsStripBackResolution,
  type PrimaryControlsStripBackResolution,
} from "#use-primary-controls-strip-back-resolution";

export type PrimaryControlsStripBackGate = Tagged<
  Pick<PrimaryControlsStripBackResolution, "fallbackHref" | "label">,
  "PrimaryControlsStripBackGate"
>;

/**
 * Whether a back control shows in this lane, and what it does. `null` is the designed hidden
 * state: no publisher and nothing to pop, a screen published `hidden`, another lane claimed the
 * control, or web (which has no native back control).
 *
 * Visibility is decided ONLY by `usePrimaryControlsStripBackResolution`, the single predicate every part of
 * the system reads (the shell's reserved row, the panels toolbar's branch, and this gate). Having
 * a host re-derive its own answer is what previously let the shell reserve space for a chevron the
 * host declined to draw, and vice versa.
 *
 * Pass `appShellPlacement` only for an app-shell lane: the default and sidebar headers each own
 * the single back slot in their header, so they ask the placement-agnostic question and render
 * whatever won.
 */
export function usePrimaryControlsStripBackGate({
  appShellPlacement,
}: {
  appShellPlacement?: PrimaryControlsStripBackAppShellPlacement;
} = {}): PrimaryControlsStripBackGate | null {
  const { fallbackHref, isVisible, label } = usePrimaryControlsStripBackResolution({
    appShellPlacement,
  });
  return isVisible ? createGate<PrimaryControlsStripBackGate>({ fallbackHref, label }) : null;
}
